import React, { useState, useEffect } from 'react';
import { Header } from '../components/layout/Header';
import { visitorsAPI } from '../lib/api';
import { Eye, Globe, RefreshCw, Activity, Users, TrendingUp, Clock } from 'lucide-react';
import { toast } from 'sonner';

const SEGMENTO_LABELS = {
  geral:     'Geral',
  gospel:    'Gospel',
  mentoria:  'Mentoria',
  mesa_ester:'Mesa de Ester',
  devocional:'Devocional',
};

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="bg-white border border-stone-200 rounded-xl p-5 flex items-center gap-4">
      <div className="p-3 rounded-xl" style={{ background: '#FDF6F0' }}>
        <Icon className="w-5 h-5" style={{ color: '#A0462D' }} />
      </div>
      <div>
        <p className="text-xs uppercase tracking-wide" style={{ color: '#A0462D' }}>{label}</p>
        <p className="text-2xl font-bold mt-0.5" style={{ color: '#2D0C10' }}>{value ?? '—'}</p>
      </div>
    </div>
  );
}

export const Visitors = () => {
  const [stats, setStats]     = useState(null);
  const [visitors, setVisitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab]         = useState('pages');

  const load = async () => {
    setLoading(true);
    try {
      const [statsRes, listRes] = await Promise.all([
        visitorsAPI.stats(),
        visitorsAPI.list({ limit: 50 }),
      ]);
      setStats(statsRes.data);
      setVisitors(listRes.data?.visitors || []);
    } catch {
      toast.error('Erro ao carregar visitantes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const byPage = stats?.by_page || [];
  const bySegmento = stats?.by_segmento || [];

  return (
    <div>
      <Header
        title="Visitantes"
        subtitle="Tráfego e comportamento na plataforma"
        actions={
          <button onClick={load} className="p-2 border border-stone-300 rounded-lg text-stone-500 hover:text-stone-700 transition-colors">
            <RefreshCw className="w-4 h-4" />
          </button>
        }
      />

      <div className="p-6 space-y-6">

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard icon={Eye}       label="Pageviews"  value={stats?.total?.toLocaleString('pt-BR')} />
          <StatCard icon={Users}     label="Visitantes" value={visitors.length?.toLocaleString('pt-BR')} />
          <StatCard icon={TrendingUp} label="Páginas únicas" value={byPage.length} />
          <StatCard icon={Activity}  label="Jornadas"  value={bySegmento.length} />
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-stone-200">
          {[
            { key: 'pages',    label: 'Páginas mais vistas' },
            { key: 'segmentos', label: 'Por jornada' },
            { key: 'recentes', label: 'Visitantes recentes' },
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                tab === t.key
                  ? 'border-[#A0462D] text-[#A0462D]'
                  : 'border-transparent text-stone-500 hover:text-stone-700'
              }`}>
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="w-6 h-6 animate-spin" style={{ color: '#A0462D' }} />
          </div>
        ) : tab === 'pages' ? (
          byPage.length === 0 ? (
            <div className="text-center py-16 text-stone-400">
              <Globe className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Nenhuma página registrada ainda.</p>
            </div>
          ) : (
            <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-stone-100 bg-stone-50">
                    <th className="text-left px-4 py-3 text-xs text-stone-500 font-medium">#</th>
                    <th className="text-left px-4 py-3 text-xs text-stone-500 font-medium">Página</th>
                    <th className="text-right px-4 py-3 text-xs text-stone-500 font-medium">Acessos</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {byPage.map((p, i) => {
                    const maxCount = byPage[0]?._id ? byPage[0].count : 1;
                    const pct = Math.round(((p.count || 0) / maxCount) * 100);
                    return (
                      <tr key={i} className="hover:bg-stone-50 transition-colors">
                        <td className="px-4 py-3 text-xs text-stone-400">{i + 1}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Globe className="w-3.5 h-3.5 text-stone-400 flex-shrink-0" />
                            <span className="font-medium text-stone-700 truncate max-w-xs">{p._id || '/'}</span>
                          </div>
                          <div className="mt-1 h-1.5 rounded-full bg-stone-100 overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${pct}%`, background: '#A0462D' }} />
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right font-bold" style={{ color: '#A0462D' }}>
                          {(p.count || 0).toLocaleString('pt-BR')}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        ) : tab === 'segmentos' ? (
          bySegmento.length === 0 ? (
            <div className="text-center py-16 text-stone-400">
              <Activity className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Nenhum dado de jornada disponível.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {bySegmento.map((s, i) => {
                const max = bySegmento[0]?.count || 1;
                const pct = Math.round((s.count / max) * 100);
                const label = SEGMENTO_LABELS[s._id] || s._id || 'Sem jornada';
                return (
                  <div key={i} className="flex items-center gap-4">
                    <span className="w-5 text-xs text-stone-400 text-right">{i + 1}</span>
                    <div className="w-28 text-sm font-medium flex-shrink-0" style={{ color: '#2D0C10' }}>{label}</div>
                    <div className="flex-1">
                      <div className="h-2 rounded-full bg-stone-100 overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: i === 0 ? '#A0462D' : '#C4724F' }} />
                      </div>
                    </div>
                    <span className="text-sm font-bold w-10 text-right" style={{ color: '#A0462D' }}>
                      {s.count}
                    </span>
                  </div>
                );
              })}
            </div>
          )
        ) : (
          visitors.length === 0 ? (
            <div className="text-center py-16 text-stone-400">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Nenhum visitante registrado ainda.</p>
            </div>
          ) : (
            <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-stone-100 bg-stone-50">
                    <th className="text-left px-4 py-3 text-xs text-stone-500 font-medium">ID</th>
                    <th className="text-left px-4 py-3 text-xs text-stone-500 font-medium">Jornada</th>
                    <th className="text-left px-4 py-3 text-xs text-stone-500 font-medium">Referência</th>
                    <th className="text-left px-4 py-3 text-xs text-stone-500 font-medium">Primeiro acesso</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {visitors.map((v, i) => (
                    <tr key={i} className="hover:bg-stone-50 transition-colors">
                      <td className="px-4 py-3 font-mono text-xs text-stone-400 truncate max-w-[100px]">{v.id?.slice(-8) || '—'}</td>
                      <td className="px-4 py-3">
                        {v.segmento ? (
                          <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: '#FDF6F0', color: '#A0462D' }}>
                            {SEGMENTO_LABELS[v.segmento] || v.segmento}
                          </span>
                        ) : <span className="text-xs text-stone-400">—</span>}
                      </td>
                      <td className="px-4 py-3 text-xs text-stone-500 truncate max-w-[180px]">{v.referrer || '—'}</td>
                      <td className="px-4 py-3 text-xs text-stone-500">
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {v.created_at ? new Date(v.created_at).toLocaleString('pt-BR', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' }) : '—'}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default Visitors;
